import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ILocation,
  CreateLocationDto,
  UpdateLocationDto,
  IFrequentDestination,
} from '@carrierbridge/shared';
import api from '@/lib/api';
import { bulkDeleteInBatches } from '@/lib/bulk-delete';

const LOCATIONS_KEY = ['locations'] as const;

export function useLocations() {
  return useQuery<ILocation[]>({
    queryKey: LOCATIONS_KEY,
    queryFn: async () => {
      const { data } = await api.get<ILocation[]>('/locations');
      return data;
    },
  });
}

export function useCreateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateLocationDto) => {
      const { data } = await api.post<ILocation>('/locations', dto);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LOCATIONS_KEY }),
  });
}

export function useUpdateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: UpdateLocationDto }) => {
      const { data } = await api.patch<ILocation>(`/locations/${id}`, dto);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LOCATIONS_KEY }),
  });
}

export function useDeleteLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/locations/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LOCATIONS_KEY }),
  });
}

/**
 * Suppression en masse depuis la console Data. Le lot est découpé aux bornes
 * que l'API accepte ({@link bulkDeleteInBatches}) : le serveur renvoie ce qui
 * est parti et ce qui a résisté, l'appelant décide du message.
 */
export function useBulkDeleteLocations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      return bulkDeleteInBatches('/locations', ids);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LOCATIONS_KEY }),
  });
}

/**
 * Destinations les plus servies depuis un lieu de départ.
 *
 * Sur un carnet de plusieurs milliers d'adresses, l'ordre alphabétique ne sait
 * rien du métier : une carrière livre presque toujours les mêmes chantiers.
 * Ce classement vient des expéditions réelles, et remonte en tête de la liste
 * de choix.
 */
export function useFrequentDestinations(originLocationId: string | null) {
  return useQuery<IFrequentDestination[]>({
    queryKey: ['locations', 'frequent-destinations', originLocationId],
    queryFn: async () => {
      const { data } = await api.get<IFrequentDestination[]>(
        `/locations/${originLocationId}/frequent-destinations`,
      );
      return data;
    },
    enabled: !!originLocationId,
  });
}
